import type {
  BaseItem,
  AdditionalResources as IAdditionalResources,
  InternalLink,
  PossibleMenuItem,
} from '@bulknutrients/bulk-shared-lib/interfaces/Menu';
import type { SanityImage } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { mergeStyles } from '@bulknutrients/bulk-shared-lib/utils';
import {
  UIContext,
  type UIContextInterface,
} from '@components/Context/UIContext/UIContext';
import { Icons } from '@components/Icons/Icons';
import { CustomImage } from '@components/Media/Image';
import { SkeletonImage, SkeletonText } from '@components/Placeholders/Skeleton';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import Link from 'next/link';
import { type ReactElement, useContext } from 'react';

const hasLink = (item: PossibleMenuItem): item is InternalLink | BaseItem =>
  'slug' in (item as InternalLink | BaseItem);

const hasImage = (item: PossibleMenuItem): item is BaseItem =>
  'image' in (item as BaseItem);

function MenuItemInner({
  data,
  onItemSelection,
  textSize,
  isLastItem,
}: {
  data: PossibleMenuItem;
  onItemSelection: (_: string) => void;
  textSize: string;
  isLastItem: boolean;
}): ReactElement {
  return (
    <div
      className="clickableNoLine gae-menu-item group flex cursor-pointer items-center"
      onClick={() => onItemSelection(data.title)}
    >
      {hasImage(data) && (
        <div className="mr-4 min-w-max rounded-xs	shadow-[0_0_0_2px_white,0_0_0_4px_hsl(0,0%,80%)] dark:shadow-[0_0_0_2px_black,0_0_0_4px_hsl(0,0%,40%)]">
          <CustomImage
            image={data.image as SanityImage}
            width={50}
            height={50}
            className="rounded-none"
          />
        </div>
      )}
      <div className="flex-auto justify-center">
        <h3>
          <strong
            className={mergeStyles(
              textSize,
              'flex justify-between pt-1 group-hover:underline group-hover:decoration-orange group-hover:decoration-3 group-active:underline group-active:decoration-orange group-active:decoration-3'
            )}
          >
            {data.title}
            {'items' in data && <Icons.Chevron.Right />}
          </strong>
        </h3>
        <Paragraph className="m-0 min-h-3 text-sm font-normal no-underline">
          {'description' in data ? data.description : ''}
        </Paragraph>
      </div>
    </div>
  );
}

const MenuItem = ({
  data,
  onItemSelection,
  textSize,
  isLastItem,
}: {
  data: PossibleMenuItem;
  onItemSelection: (_: string) => void;
  textSize: string;
  isLastItem: boolean;
}): ReactElement => {
  return (
    <li
      key={data.title}
      className="py-4 border-b border-grey/50 border-via-grey-mid dark:border-via-grey-light"
    >
      {hasLink(data) && typeof data.slug === 'string' ? (
        <Link
          prefetch={process.env.NEXT_PUBLIC_PREFETCH === 'true'}
          href={data.slug}
        >
          <MenuItemInner
            data={data}
            onItemSelection={onItemSelection}
            textSize={textSize}
            isLastItem={isLastItem}
          />
        </Link>
      ) : (
        <MenuItemInner
          data={data}
          onItemSelection={onItemSelection}
          textSize={textSize}
          isLastItem={isLastItem}
        />
      )}
    </li>
  );
};

export function MenuItemSkeleton(): ReactElement {
  return (
    <div className="clickableNoLine gae-menu-item group flex cursor-pointer items-center">
      <div className="mr-4 min-w-max rounded-xs	shadow-[0_0_0_2px_white,0_0_0_4px_hsl(0,0%,80%)] dark:shadow-[0_0_0_2px_black,0_0_0_4px_hsl(0,0%,40%)]">
        <SkeletonImage widthPixels={50} />
      </div>
      <div className="flex flex-col w-full">
        <SkeletonText heightPixels={24} textClasses="text-xl" />
        <SkeletonText heightPixels={16} textClasses="text-sm" />
      </div>
    </div>
  );
}

export const MenuContent = ({
  menuToDisplay,
  onItemSelection,
  additionalResources,
  animationDir,
}: {
  menuToDisplay: PossibleMenuItem[];
  onItemSelection: (_: string) => void;
  additionalResources: IAdditionalResources | undefined;
  animationDir: 'left' | 'right';
}): ReactElement => {
  const { menuBreadcrumbs } = useContext<UIContextInterface>(UIContext);

  return (
    <nav
      className={
        animationDir === 'right'
          ? 'staggered-delay-from-right'
          : 'staggered-delay-from-left'
      }
    >
      <ul>
        {menuToDisplay?.map((i, idx) => {
          return (
            <MenuItem
              data={i}
              key={`${i.title}_${idx}`}
              onItemSelection={onItemSelection}
              textSize={menuBreadcrumbs.length > 0 ? 'text-xl' : 'text-2xl'}
              isLastItem={idx === menuToDisplay.length - 1}
            />
          );
        })}
        {/* additional resources need to be here so they fall within the staggered delay tree */}
        {additionalResources && (
          <>
            <li
              key={additionalResources.title}
              className="mt-6 mb-2 list-none text-lg italic"
            >
              <strong>{additionalResources.title}</strong>
            </li>
            {additionalResources.items.map((i) => (
              <li
                key={i.title}
                className="py-4 border-b border-grey/50 border-via-grey-mid dark:border-via-grey-light"
              >
                <TextLink href={i.slug}>{i.title}</TextLink>
              </li>
            ))}
          </>
        )}
      </ul>
    </nav>
  );
};
